import { addDays } from "./dates";
import { parseScaled } from "./decimal";
import { DomainError } from "./errors";

/**
 * Logística (T3.4, RF-VEN-10): provedores (Correios com contrato próprio e Melhor Envio), situações do objeto de
 * postagem e as transições do rastreio, eventos dos provedores → situação, código de objeto dos Correios (S10),
 * pacote (inteiros: gramas e centímetros), valores decimais do provedor ↔ centavos e prazos. Sem I/O.
 */

export const LOGISTICS_PROVIDER_IDS = ["correios", "melhorenvio"] as const;
export type LogisticsProvider = (typeof LOGISTICS_PROVIDER_IDS)[number];
export const isLogisticsProvider = (v: unknown): v is LogisticsProvider =>
  typeof v === "string" && (LOGISTICS_PROVIDER_IDS as readonly string[]).includes(v);
export const LOGISTICS_PROVIDER_LABELS: Record<LogisticsProvider, string> = {
  correios: "Correios",
  melhorenvio: "Melhor Envio",
};
/** "dos Correios", "do Melhor Envio" (mensagens). */
export const LOGISTICS_PROVIDER_OF: Record<LogisticsProvider, string> = {
  correios: "dos Correios",
  melhorenvio: "do Melhor Envio",
};

/** Integrações por provedor e empresa. */
export const MAX_LOGISTICS_INTEGRATIONS = 5;
/** Objetos por remessa (antiga PLP). */
export const MAX_BATCH_SHIPMENTS = 200;
/** Objetos por consulta de rastreio (SRO aceita até 50). */
export const MAX_TRACK_BATCH = 50;
/** Sincronização manual: até tantos objetos selecionados. */
export const MAX_TRACK_SELECTION = 100;
/** O cron só rastreia de novo depois disto. */
export const TRACK_INTERVAL_MS = 2 * 3600_000;
/** Validade da autorização de postagem da reversa (dias). */
export const REVERSE_DAYS_DEFAULT = 30;
export const REVERSE_DAYS_MIN = 7;
export const REVERSE_DAYS_MAX = 90;
/** Token CWS (24 h): renovar só nos 30 min finais. */
export const CORREIOS_TOKEN_MARGIN_MS = 30 * 60_000;

/** Serviços padrão do contrato dos Correios (código do produto). */
export const CORREIOS_DEFAULT_SERVICES: readonly { code: string; name: string }[] = [
  { code: "03220", name: "SEDEX" },
  { code: "03298", name: "PAC" },
];
export const isCorreiosServiceCode = (v: unknown): v is string => typeof v === "string" && /^\d{5}$/.test(v);

export const SHIPMENT_STATUSES = [
  "pending",
  "label_ready",
  "posted",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "failed",
  "returned",
  "expired",
  "error",
  "canceling",
  "canceled",
] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

const LABELS: Record<ShipmentStatus, string> = {
  pending: "Aguardando etiqueta",
  label_ready: "Etiqueta gerada",
  posted: "Postado",
  in_transit: "Em trânsito",
  out_for_delivery: "Saiu para entrega",
  delivered: "Entregue",
  failed: "Entrega não realizada",
  returned: "Devolvido ao remetente",
  expired: "Expirado",
  error: "Erro na etiqueta",
  canceling: "Cancelando",
  canceled: "Cancelado",
};
const REVERSE_LABELS: Partial<Record<ShipmentStatus, string>> = {
  pending: "Aguardando autorização",
  label_ready: "Autorização emitida",
  posted: "Postado pelo cliente",
  delivered: "Recebido de volta",
  error: "Erro na autorização",
};
export const shipmentStatusLabel = (s: ShipmentStatus, reverse: boolean): string =>
  (reverse ? REVERSE_LABELS[s] : undefined) ?? LABELS[s];

const FINAL: ReadonlySet<ShipmentStatus> = new Set(["delivered", "returned", "expired", "canceled"]);
const TRACKABLE: ReadonlySet<ShipmentStatus> = new Set([
  "label_ready",
  "posted",
  "in_transit",
  "out_for_delivery",
  "failed",
]);
const CANCELABLE: ReadonlySet<ShipmentStatus> = new Set(["pending", "error", "label_ready"]);
export const isFinalShipment = (s: ShipmentStatus) => FINAL.has(s);
export const isTrackableShipment = (s: ShipmentStatus) => TRACKABLE.has(s);
/** Cancelar só antes da postagem (depois, o objeto já está com o transportador). */
export const canCancelShipment = (s: ShipmentStatus) => CANCELABLE.has(s);

const RANK: Record<ShipmentStatus, number> = {
  pending: 0,
  error: 0,
  label_ready: 1,
  canceling: 1,
  posted: 2,
  in_transit: 3,
  failed: 3,
  out_for_delivery: 4,
  delivered: 5,
  returned: 5,
  expired: 5,
  canceled: 5,
};

/**
 * Situação depois de um evento de rastreio. Final não muda; entregue e devolvido valem sempre; cancelado/expirado no
 * provedor só antes da postagem; "não realizada" só depois da postagem e volta ao trânsito numa nova tentativa; o
 * resto só avança.
 */
export function nextTrackedStatus(current: ShipmentStatus, incoming: ShipmentStatus | null): ShipmentStatus {
  if (!incoming || incoming === current || FINAL.has(current)) return current;
  if (incoming === "delivered" || incoming === "returned") return incoming;
  if (incoming === "canceled" || incoming === "expired") return RANK[current] < 2 ? incoming : current;
  if (incoming === "failed") return RANK[current] >= 2 ? incoming : current;
  if (current === "failed") return RANK[incoming] >= 3 ? incoming : current;
  return RANK[incoming] > RANK[current] ? incoming : current;
}

// ---------- Correios ----------

const S10_WEIGHTS = [8, 6, 4, 2, 3, 5, 9, 7];
/** Código de objeto (S10/UPU: 2 letras, 8 dígitos, dígito verificador, 2 letras) com o dígito conferido. */
export function correiosTrackingValid(code: string): boolean {
  const m = /^[A-Z]{2}(\d{8})(\d)[A-Z]{2}$/.exec(code);
  if (!m) return false;
  const sum = [...m[1]!].reduce((acc, d, i) => acc + Number(d) * S10_WEIGHTS[i]!, 0);
  const r = sum % 11;
  const dv = r === 0 ? 5 : r === 1 ? 0 : 11 - r;
  return Number(m[2]) === dv;
}

const CORREIOS_TRANSIT = new Set(["RO", "DO", "TRI", "PAR", "PMT", "LDE", "LDI"]);
const CORREIOS_RETURNED_TYPES = new Set(["23", "28"]);
/** Evento do SRO (código + tipo) → situação; null = só registra o evento. */
export function correiosEventStatus(code: string, type: string): ShipmentStatus | null {
  if (code === "PO") return "posted";
  if (CORREIOS_TRANSIT.has(code)) return "in_transit";
  if (code === "OEC") return "out_for_delivery";
  if (code === "BDE" || code === "BDI" || code === "BDR") {
    if (type === "01") return "delivered";
    return CORREIOS_RETURNED_TYPES.has(type) ? "returned" : "failed";
  }
  if (code === "FC" && (type === "47" || type === "49")) return "returned";
  return null;
}

// ---------- Melhor Envio ----------

const ME_STATUS: Record<string, ShipmentStatus> = {
  pending: "label_ready",
  released: "label_ready",
  generated: "label_ready",
  posted: "posted",
  delivered: "delivered",
  undelivered: "failed",
  canceled: "canceled",
  cancelled: "canceled",
  expired: "expired",
};
/** Situação da etiqueta no Melhor Envio → situação; null = só registra (suspensa, pausada…). */
export const melhorEnvioStatus = (status: string): ShipmentStatus | null => ME_STATUS[status] ?? null;

// ---------- pacote ----------

export type PackageDims = { weightG: number; heightCm: number; widthCm: number; lengthCm: number };
export type PackageItem = {
  qtyQ4: number;
  grossWeightG: number | null;
  widthMm: number | null;
  heightMm: number | null;
  depthMm: number | null;
};
/** Mínimo do pacote nos Correios (comprimento × largura × altura). */
export const PACKAGE_MIN = { lengthCm: 16, widthCm: 11, heightCm: 2 } as const;
export const PACKAGE_MAX_WEIGHT_G = 30_000;
export const PACKAGE_MAX_SIDE_CM = 100;
export const PACKAGE_MAX_SUM_CM = 200;
/** Peso de um item sem peso bruto no cadastro. */
export const DEFAULT_ITEM_WEIGHT_G = 300;

const ceilDiv = (a: number, b: number) => Math.floor((a + b - 1) / b);

/**
 * Pacote sugerido pelos itens: peso = Σ peso bruto × quantidade (q4, para cima; sem peso = 300 g por unidade),
 * comprimento e largura = maiores medidas, altura = soma das menores empilhadas (até 100 cm); mínimo dos Correios.
 */
export function suggestPackage(items: readonly PackageItem[]): PackageDims {
  let weightG = 0;
  let lengthCm = 0;
  let widthCm = 0;
  let heightCm = 0;
  for (const it of items) {
    weightG += ceilDiv((it.grossWeightG ?? DEFAULT_ITEM_WEIGHT_G) * it.qtyQ4, 10_000);
    const dims = [it.widthMm, it.heightMm, it.depthMm];
    if (dims.some((d) => !d || d <= 0)) continue;
    const [l, w, h] = (dims as number[]).map((mm) => ceilDiv(mm, 10)).sort((a, b) => b - a) as [
      number,
      number,
      number,
    ];
    lengthCm = Math.max(lengthCm, l);
    widthCm = Math.max(widthCm, w);
    heightCm += h * ceilDiv(it.qtyQ4, 10_000);
  }
  return {
    weightG: weightG || DEFAULT_ITEM_WEIGHT_G,
    lengthCm: Math.max(PACKAGE_MIN.lengthCm, lengthCm),
    widthCm: Math.max(PACKAGE_MIN.widthCm, widthCm),
    heightCm: Math.min(PACKAGE_MAX_SIDE_CM, Math.max(PACKAGE_MIN.heightCm, heightCm)),
  };
}

const bad = (msg: string): never => {
  throw new DomainError("invalid_package", msg);
};
const side = (v: number, min: number, name: string) => {
  if (!Number.isInteger(v) || v < min || v > PACKAGE_MAX_SIDE_CM)
    bad(`A ${name} do pacote deve ser de ${min} a ${PACKAGE_MAX_SIDE_CM} cm.`);
};

/** Limites do pacote (Correios; o Melhor Envio aceita os mesmos na maior parte dos serviços). */
export function validatePackage(p: PackageDims): void {
  if (!Number.isInteger(p.weightG) || p.weightG < 1) bad("Informe o peso do pacote em gramas.");
  if (p.weightG > PACKAGE_MAX_WEIGHT_G) bad("O pacote passa de 30 kg.");
  if (!Number.isInteger(p.lengthCm) || p.lengthCm < PACKAGE_MIN.lengthCm || p.lengthCm > PACKAGE_MAX_SIDE_CM)
    bad(`O comprimento do pacote deve ser de ${PACKAGE_MIN.lengthCm} a ${PACKAGE_MAX_SIDE_CM} cm.`);
  side(p.widthCm, PACKAGE_MIN.widthCm, "largura");
  side(p.heightCm, PACKAGE_MIN.heightCm, "altura");
  if (p.lengthCm + p.widthCm + p.heightCm > PACKAGE_MAX_SUM_CM)
    bad("A soma das medidas do pacote passa de 200 cm.");
}

// ---------- valores ----------

/** Valor decimal do provedor (texto "37.79"/"27,90" ou número JSON) em centavos; negativo ou inválido = erro. */
export function shippingAmountCents(v: string | number, source: string): number {
  const s = typeof v === "number" ? (Number.isFinite(v) && v >= 0 ? String(v) : "") : v.trim();
  try {
    if (!s || s.startsWith("-")) throw new Error();
    return parseScaled(s, 2, "invalid_amount");
  } catch {
    throw new DomainError("invalid_amount", `Valor inválido ${source}: ${String(v).slice(0, 40)}`);
  }
}

/** Centavos → "1234.56" (só a formatação na borda com o provedor). */
export const centsToDecimalText = (cents: number) =>
  `${Math.trunc(cents / 100)}.${String(cents % 100).padStart(2, "0")}`;
/** Gramas → "0.300" (kg com 3 casas). */
export const gramsToKgText = (g: number) => `${Math.trunc(g / 1000)}.${String(g % 1000).padStart(3, "0")}`;

// ---------- prazos ----------

/** Último dia para o cliente postar a reversa (data de São Paulo). */
export const reverseExpiresAt = (today: string, days: number) => addDays(today, days);
/** O objeto precisa ser rastreado de novo (nunca rastreado ou há mais de 2 h). */
export const trackingDue = (trackedAt: string | null, now: number) =>
  !trackedAt || now - Date.parse(trackedAt) >= TRACK_INTERVAL_MS;
